import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SEOMeta from "@/components/SEOMeta";
import SchemaMarkup from "@/components/SchemaMarkup";
import FAQ from "@/components/FAQ";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { 
  Paintbrush, 
  Shield, 
  CheckCircle2,
  Clock,
  Phone,
  Mail,
  MapPin,
  Award,
  Home,
  Building2,
  Palette,
  Sparkles,
  Users,
  ThumbsUp
} from "lucide-react";
import heroImage from "@/assets/painting-services-hero.jpg";
import interiorPaintingImage from "@/assets/interior-painting-residential.jpg";
import exteriorPaintingImage from "@/assets/exterior-painting-house-new.jpg";
import commercialInteriorImage from "@/assets/commercial-painting-interior.jpg";
import commercialExteriorImage from "@/assets/commercial-exterior-painting.jpg";

const PaintingServices = () => {
  const faqData = [
    {
      question: "How much does interior painting cost in Toronto?",
      answer: "Interior painting costs vary based on room size, ceiling height, and surface condition. On average, expect $2-$6 per square foot for walls and ceilings. A standard 12x12 room typically costs $400-$900 including materials. We provide free detailed quotes for all projects in Toronto, Hamilton, and Niagara Region."
    },
    {
      question: "What's the best time of year for exterior painting in Ontario?",
      answer: "The ideal time for exterior painting in Ontario is late spring through early fall (May-September) when temperatures consistently stay above 10°C (50°F). Dry conditions and moderate temperatures ensure optimal paint adhesion and drying. We can work year-round for interior projects."
    },
    {
      question: "How long does it take to paint a house?",
      answer: "Interior painting of a 3-bedroom home typically takes 3-5 days. Exterior painting takes 5-10 days depending on home size and weather. Commercial projects vary based on square footage and complexity. We provide detailed timelines during our free consultation."
    },
    {
      question: "Do you provide color consultation services?",
      answer: "Yes! Our experienced team offers complimentary color consultation to help you select the perfect paint colors for your space. We can provide samples, discuss color psychology, and recommend palettes that complement your décor and lighting."
    },
    {
      question: "What paint brands do you use?",
      answer: "We use premium paint brands including Benjamin Moore, Sherwin-Williams, and Behr. These professional-grade paints offer superior coverage, durability, and finish quality. We'll recommend the best product for your specific project and budget."
    },
    {
      question: "Do I need to move furniture before painting?",
      answer: "We handle furniture protection and can work around most furniture. For interior projects, we move smaller items and protect larger pieces with drop cloths. For best results and efficiency, we recommend clearing the room when possible. We always protect flooring and restore everything after completion."
    }
  ];

  const residentialServices = [
    {
      title: "Interior Painting",
      description: "Transform your living spaces with professional interior painting",
      icon: Home,
      features: [
        "Living rooms, bedrooms, and common areas",
        "Kitchen and bathroom painting",
        "Ceiling and trim painting",
        "Feature walls and accent colors",
        "Wallpaper removal and prep",
        "Low-VOC and eco-friendly paint options"
      ]
    },
    {
      title: "Exterior Painting",
      description: "Protect and beautify your home's exterior",
      icon: Building2,
      features: [
        "Siding and trim painting",
        "Deck and fence staining",
        "Door and window painting",
        "Garage and shed painting",
        "Power washing and surface prep",
        "Weather-resistant paint systems"
      ]
    },
    {
      title: "Specialty Finishes",
      description: "Custom painting techniques and decorative finishes",
      icon: Palette,
      features: [
        "Faux finishing and texturing",
        "Cabinet painting and refinishing",
        "Crown molding and trim work",
        "Popcorn ceiling removal",
        "Wood staining and varnishing",
        "High-gloss and matte finishes"
      ]
    }
  ];

  const commercialServices = [
    {
      title: "Office & Retail Painting",
      description: "Professional painting for commercial spaces",
      icon: Building2,
      features: [
        "Office buildings and workspaces",
        "Retail stores and showrooms",
        "Reception areas and lobbies",
        "Conference and meeting rooms",
        "After-hours and weekend scheduling",
        "Minimal disruption to operations"
      ]
    },
    {
      title: "Industrial Painting",
      description: "Specialized coatings for industrial facilities",
      icon: Shield,
      features: [
        "Warehouses and manufacturing facilities",
        "Industrial equipment painting",
        "Epoxy floor coatings",
        "High-traffic area solutions",
        "Safety line painting",
        "Corrosion-resistant coatings"
      ]
    },
    {
      title: "Property Management",
      description: "Ongoing painting services for multi-unit properties",
      icon: Users,
      features: [
        "Apartment and condo buildings",
        "Common area maintenance",
        "Unit turnover painting",
        "Scheduled maintenance programs",
        "Bulk pricing discounts",
        "Property manager coordination"
      ]
    }
  ];

  const benefits = [
    {
      icon: Award,
      title: "Expert Craftsmanship",
      description: "Over 20 years of professional painting experience with meticulous attention to detail"
    },
    {
      icon: Shield,
      title: "Quality Materials",
      description: "Premium paint brands and professional-grade tools for lasting, beautiful results"
    },
    {
      icon: Clock,
      title: "On-Time Service",
      description: "Projects completed on schedule with minimal disruption to your home or business"
    },
    {
      icon: ThumbsUp,
      title: "Satisfaction Guaranteed",
      description: "We stand behind our work with comprehensive warranties and customer satisfaction guarantee"
    }
  ];

  const serviceAreas = [
    { name: "Toronto", description: "Downtown, North York, Scarborough, Etobicoke" },
    { name: "Hamilton", description: "Downtown, Stoney Creek, Dundas, Ancaster" },
    { name: "Niagara Region", description: "St. Catharines, Niagara Falls, Welland, Grimsby" },
    { name: "GTA", description: "Mississauga, Brampton, Markham, Vaughan, Oakville" }
  ];

  return (
    <>
      <SEOMeta 
        title="Painting Contractor — Interior & Exterior | Niagara • Hamilton • GTA"
        description="Professional painters for residential and commercial projects across Niagara, Hamilton & the GTA. Interior, exterior and finishing services."
        keywords="painting contractor Niagara, Hamilton painters, GTA painting service, interior painting, exterior painting, commercial painting, spray finishing"
        canonicalUrl="https://blueknightcontracting.ca/services/painting"
        ogImage={heroImage}
      />
      
      <SchemaMarkup
        type="Service"
        data={{
          serviceType: "Professional Painting Services",
          serviceName: "Residential & Commercial Painting",
          services: [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Interior Painting Toronto",
                "description": "Professional interior painting services for homes and businesses with premium paint brands."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Exterior Painting Hamilton",
                "description": "Expert exterior house painting and protection with weather-resistant paint systems."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Commercial Painting Niagara",
                "description": "Professional commercial and industrial painting with flexible scheduling."
              }
            }
          ]
        }}
      />

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
            }}
          />
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Professional Painting Services
            </h1>
            <p className="text-xl md:text-2xl mb-8">
              Interior & Exterior Painting | Toronto, Hamilton & Niagara Region
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="default" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="bg-white/10 text-white border-white hover:bg-white/20">
                <a href="tel:4165552583">Call (416) 555-2583</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  Ontario's Trusted Professional Painters
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Blue Knight Contracting has been providing exceptional painting services throughout the Greater Toronto Area, Hamilton, and Niagara Region for over 20 years. Our team of skilled painters delivers flawless finishes for residential and commercial properties using premium materials and proven techniques.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  From interior room painting to complete exterior home makeovers, we handle projects of all sizes with professionalism, attention to detail, and respect for your property. All work is fully guaranteed with comprehensive warranties.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Licensed & Insured</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Free Color Consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Premium Paints</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <benefit.icon className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Residential Services */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Residential Painting Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Transform your home with professional interior and exterior painting
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {residentialServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Residential Showcase */}
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={interiorPaintingImage}
                    alt="Professional interior painting of Ontario home living room with paint roller"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Interior Painting</h3>
                  <p className="text-muted-foreground mb-4">
                    Expert interior painting for every room in your home. From single accent walls to whole-house painting projects, we deliver flawless results with clean lines and smooth finishes.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={exteriorPaintingImage}
                    alt="Professional exterior house painting in Ontario with painters on ladders"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Exterior Painting</h3>
                  <p className="text-muted-foreground mb-4">
                    Protect and beautify your home's exterior with premium weather-resistant paints. Includes power washing, surface prep, priming, and multi-coat application for long-lasting results.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Commercial Services */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Commercial Painting Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Professional painting for offices, retail, industrial, and multi-unit properties
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {commercialServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Commercial Showcase */}
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={commercialInteriorImage}
                    alt="Commercial interior painting crew painting office space in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Commercial Interior Painting</h3>
                  <p className="text-muted-foreground mb-4">
                    Professional painting for offices, retail stores, and commercial facilities. Flexible scheduling including after-hours and weekend work to minimize business disruption.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={commercialExteriorImage}
                    alt="Commercial exterior building painting in Ontario with professional crew"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Commercial Exterior Painting</h3>
                  <p className="text-muted-foreground mb-4">
                    Transform your commercial property's curb appeal with professional exterior painting. Durable commercial-grade coatings that withstand Ontario weather and heavy use.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Painting Process</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Professional service from consultation to final walkthrough
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: "01",
                  title: "Free Consultation",
                  description: "We assess your project, discuss color options, and provide a detailed written estimate with no obligation."
                },
                {
                  step: "02",
                  title: "Surface Preparation",
                  description: "Thorough cleaning, repairs, sanding, and priming to ensure optimal paint adhesion and finish quality."
                },
                {
                  step: "03",
                  title: "Professional Painting",
                  description: "Expert application using premium paints and professional techniques for smooth, even coverage."
                },
                {
                  step: "04",
                  title: "Final Inspection",
                  description: "Detailed walkthrough, touch-ups as needed, and complete cleanup leaving your space pristine."
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="inline-block bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Choose Blue Knight Painting?</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Paintbrush className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Expert Painters</h3>
                <p className="text-muted-foreground">
                  Skilled professionals with decades of combined painting experience
                </p>
              </Card>

              <Card className="text-center p-6">
                <Palette className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Premium Paints</h3>
                <p className="text-muted-foreground">
                  Benjamin Moore, Sherwin-Williams, and other top-tier paint brands
                </p>
              </Card>

              <Card className="text-center p-6">
                <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Immaculate Finish</h3>
                <p className="text-muted-foreground">
                  Meticulous attention to detail with clean lines and smooth surfaces
                </p>
              </Card>

              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Fully Warranted</h3>
                <p className="text-muted-foreground">
                  Comprehensive warranties and satisfaction guarantee on all work
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Serving Southern Ontario</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Professional painting services throughout the GTA, Hamilton, and Niagara Region
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => (
                <Card key={index} className="text-center">
                  <CardHeader>
                    <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                    <CardTitle className="text-xl">{area.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{area.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Transform Your Space with Fresh Paint?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Get a free consultation and quote today. Experience the Blue Knight difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/contact">Request Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white/20" asChild>
                <a href="tel:4165552583">
                  <Phone className="mr-2 h-5 w-5" />
                  (416) 555-2583
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <FAQ 
              title="Frequently Asked Questions About Painting Services"
              description="Get answers to common questions about residential and commercial painting"
              faqs={faqData}
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl mb-2">
                  Contact Blue Knight Contracting Today
                </CardTitle>
                <CardDescription className="text-base">
                  Professional painting services in Toronto, Hamilton, Niagara & GTA
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <Phone className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Call Us</p>
                    <a href="tel:4165552583" className="text-primary hover:underline">
                      (416) 555-2583
                    </a>
                  </div>
                  <div>
                    <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Email Us</p>
                    <a href="mailto:info@blueknightcontracting.ca" className="text-primary hover:underline">
                      info@blueknightcontracting.ca
                    </a>
                  </div>
                  <div>
                    <Paintbrush className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Get Started</p>
                    <Link to="/contact" className="text-primary hover:underline">
                      Free Consultation
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PaintingServices;
